import React, { useEffect, useRef } from 'react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, LabelList } from 'recharts'

const PDFBarChart2 = ({ data = [], width = 750, height = 500, onLoaded }) => {
  const chartRef = useRef()
  const hasLoaded = useRef(false)

  const colorPalette = ['#ff9900', '#52b76a', '#3498db', '#a4c619', '#e74c3c']

  // 取最近 5 筆（保留原陣列順序，允許重複月份）
  const selectedRows = data.slice(-5)

  // 將每筆 selectedRows 轉成 YYYY/MM 標籤
  const selectedMonths = selectedRows.map(item => {
    const date = new Date(item.scheduledStartTime)
    return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}`
  })

  // 為了避免 dataKey 重複，我們建立唯一的 dataKey，但 label 保留月份字串
  const dataKeys = selectedMonths.map((m, idx) => ({ key: `${m}_${idx}`, label: m }))

  const behaviors = [
    { key: 'openEmail', label: '開啟信件' },
    { key: 'linkedClicked', label: '點擊連結' },
    { key: 'attachmentOpened', label: '開啟附件' },
    { key: 'enterData', label: '輸入資料' },
  ]

  // 根據 selectedRows（依 index）來填值，不再用 find()
  const transformedData = behaviors.map(({ key, label }) => {
    const behaviorData = { behavior: label }
    selectedRows.forEach((row, idx) => {
      const dk = dataKeys[idx].key // 唯一 dataKey
      if (row && row.groupNumber && row.groupNumber !== 0) {
        behaviorData[dk] = Number(((row[key] / row.groupNumber) * 100).toFixed(2))
      } else {
        behaviorData[dk] = 0
      }
    })
    return behaviorData
  })

  // 判斷是否全為 0（以 selectedRows 為準）
  const allZero =
    selectedRows.length === 0 ||
    selectedRows.every(
      item =>
        item.openEmail === 0 &&
        item.linkedClicked === 0 &&
        item.attachmentOpened === 0 &&
        item.enterData === 0,
    )

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!hasLoaded.current) {
        hasLoaded.current = true
        onLoaded?.(chartRef.current)
      }
    }, 100)
    return () => clearTimeout(timer)
  }, [onLoaded])

  return (
    <div
      ref={chartRef}
      style={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '20px',
        border: '4px solid rgb(182, 180, 180)',
        width: '100%',
        boxSizing: 'border-box',
        boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.3)',
        overflow: 'hidden',
      }}
    >
      <BarChart
        layout="vertical"
        width={width}
        height={height}
        data={transformedData}
        margin={{ top: 20, right: 60, left: -30, bottom: 50 }}
      >
        {!allZero && <CartesianGrid strokeDasharray="3 3" horizontal={false} />}
        <XAxis
          type="number"
          tickFormatter={value => `${value}%`}
          fontSize={14}
          // domain={[0, 100]}
          // ticks={[0, 20, 40, 60, 80, 100]}
          // 動態 domain
          domain={[
            0,
            Math.ceil(
              Math.max(...transformedData.flatMap(item => dataKeys.map(dk => item[dk.key] || 0))) /
                10,
            ) * 10,
          ]}
          // 動態 ticks
          ticks={(() => {
            const max =
              Math.ceil(
                Math.max(
                  ...transformedData.flatMap(item => dataKeys.map(dk => item[dk.key] || 0)),
                ) / 10,
              ) * 10
            const interval = max <= 50 ? 2 : 5
            const result = []
            for (let i = 0; i <= max; i += interval) result.push(i)
            if (result[result.length - 1] < max) result.push(max)
            return result
          })()}
          tick={({ x, y, payload }) => (
            <text
              x={x}
              y={y + 15} // 調整垂直位置
              textAnchor="middle"
              fill="#666"
              fontSize={14}
              fontWeight="bold"
            >
              {`${payload.value}%`}
            </text>
          )}
        />
        <YAxis type="category" dataKey="behavior" tick={{ fontSize: 16 }} width={120} />
        <Tooltip />
        {dataKeys.map((dk, index) => (
          <Bar
            key={dk.key}
            dataKey={dk.key}
            fill={colorPalette[index % colorPalette.length]}
            name={dk.label}
            barSize={25}
          >
            <LabelList
              dataKey={dk.key}
              position="right"
              formatter={value => `${parseFloat(value).toFixed(2)}%`}
              fill="#000000"
              fontSize={13}
              offset={8}
            />
          </Bar>
        ))}
        <Legend
          layout="horizontal"
          align="center"
          verticalAlign="bottom"
          wrapperStyle={{
            fontSize: '16px',
            marginBottom: -50,
          }}
          itemMargin={{ horizontal: 20, vertical: 10 }}
        />
        {allZero && (
          <text
            x={width / 2}
            y={height / 2}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={20}
            fontFamily="Noto Sans"
            fill="#000"
            fontWeight="bold"
          >
            尚無歷史釣中數據
          </text>
        )}
      </BarChart>
    </div>
  )
}

export default PDFBarChart2
